import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { InferenceStatus, LayerAlias, UnitTypes, UserPolices } from "constant";
import features from "features";
import { spendTime } from "helpers/spendTime";
import usePolicy from "hooks/usePolicy";
import { restrictToParentVerticalBounds } from "helpers/restrictToParentVerticalBounds";
import { RootStateInterface } from "reducer";
import { LayersManagerStateInterface } from "scenes/LayersManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import ShowHideButton from "components/buttons/ShowHideButton/ShowHideButton";
import SortableItem from "./SortableItem";

import ModalWrapper from "components/modals/ModalWrapper/ModalWrapper";

// import EyeCloseGrayIcon from "assets/icons/eye-close-gray.svg";
// import EyeOpenGrayIcon from "assets/icons/eye-open-gray.svg";
import ArrowLeftIcon from "assets/icons/arrow-left-small.svg";
import MarkersFlagOffIcon from "assets/icons/markers-flag-off.svg";
import MarkersFlagOnIcon from "assets/icons/markers-flag.svg";
import PlusIcon from "assets/icons/plus-black.svg";

import ProviderFolder from "./components/ProviderFolder/ProviderFolder";

import styles from "./provider-folders-files-manager-modal.module.scss";

export interface ProviderFoldersFilesManagerModalInterface {
  title?: string;
  description?: string;
  isDisableClose?: boolean;
  acceptButtonLabel?: string;
  acceptButtonAction?: () => void;
  isWithProgress?: boolean;
  id: string;
  alias: LayerAlias;
  initProviders: any;
  onHide: (updatedProviders: any) => void;
}

const ProviderFoldersFilesManagerModal = () => {
  const [isFolderView, setIsFolderView] = useState(false);
  const [folderId, setFolderId] = useState("");

  const dispatch = useDispatch();
  const { hasPolicy } = usePolicy();
  const sensors = useSensors(useSensor(PointerSensor));

  const {
    title,
    description,
    isDisableClose,
    alias,
    id,
    initProviders,
    onHide
  } = useSelector<
    RootStateInterface,
    ProviderFoldersFilesManagerModalInterface
  >((state) => state.modal.modalProps);

  const { providers } = useSelector<
    RootStateInterface,
    LayersManagerStateInterface
  >((state) => state.layersManager);

  const providersIds = useMemo(
    () => providers.map((el) => el.id.toString()),
    [providers]
  );

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const newIndex = over.data.current.sortable.index;

        dispatch(
          features.layersManager.actions.reorderProviderRequest({
            params: { id, alias, providerId: active.id },
            fields: { index: newIndex }
          })
        );
      }
    },
    [alias, dispatch, id]
  );

  const onDelete = useCallback(
    (providerId, isOnlyGeocore) => () => {
      dispatch(
        features.layersManager.actions.deleteProviderRequest({
          params: { id, alias, providerId },
          fields: { isOnlyGeocore }
        })
      );
    },
    [alias, dispatch, id]
  );

  useEffect(() => {
    dispatch(
      features.layersManager.actions.setProviders({
        params: { alias, id },
        providers: initProviders
      })
    );
  }, [alias, dispatch, id, initProviders]);

  useEffect(() => {
    return () => {
      dispatch(features.layersManager.actions.clearProvidersState(alias));
    };
  }, [alias, dispatch]);

  const inferenceDurationTime = useCallback((inference) => {
    if (!inference) return "";

    const { status, data } = inference;

    if (
      status === InferenceStatus.PROCESSING ||
      status === InferenceStatus.DONE
    ) {
      return spendTime({
        startTime: data.stats?.inference_time_start_at,
        endTime: data.stats?.inference_time_end_at
      });
    }
  }, []);
  const uploadDurationTime = useCallback((inference) => {
    if (!inference) return "";

    const { data } = inference;

    if (data)
      return spendTime({
        startTime: data.stats?.upload_time_start_at,
        endTime: data.stats?.upload_time_end_at
      });
  }, []);

  const onUpdateVisibilityProvidersMarkers = useCallback(
    (provider) => () => {
      dispatch(
        features.unitsManager.actions.clearUnits({
          params: {
            type: UnitTypes.POINT,
            alias
          }
        })
      );
      dispatch(
        features.layersManager.actions.setProvidersMarkersIsActive({
          providerId: provider.id,
          isActiveMarkers: !provider?.isActiveMarkers,
          alias
        })
      );
    },
    [dispatch, alias]
  );

  const isAtLeastOneVisibleProvidersMarkers = useMemo(
    () => providers.some((item) => item.isActiveMarkers),
    [providers]
  );

  const onUpdateVisibilityAllProvidersMarkers = useCallback(() => {
    dispatch(
      features.unitsManager.actions.clearUnits({
        params: {
          type: UnitTypes.POINT,
          alias
        }
      })
    );
    dispatch(
      features.layersManager.actions.setAllProvidersMarkersIsActive({
        isActiveMarkers: !isAtLeastOneVisibleProvidersMarkers,
        alias
      })
    );
  }, [dispatch, alias, isAtLeastOneVisibleProvidersMarkers]);

  const onOpenFolder = useCallback(
    (providerId) => () => {
      setFolderId(providerId);
      setIsFolderView(true);
      dispatch(
        features.layersManager.actions.fetchProvidersPhotosRequest({
          params: {
            projectId: id,
            alias,
            provider: providerId
          }
        })
      );
    },
    [alias, dispatch, id]
  );
  const onCreateFolder = useCallback(() => {
    setIsFolderView(true);
  }, []);
  const onCloseFolder = useCallback(() => {
    setIsFolderView(false);
    setFolderId("");
  }, []);

  const onPrepareProvider = useCallback(
    (providerId) => () => {
      dispatch(
        features.layersManager.actions.prepareProviderInferenceRequest({
          params: { id, alias, providerId }
        })
      );
    },
    [alias, dispatch, id]
  );

  const onClose = useCallback(() => {
    onHide(providers);
    dispatch(features.modal.actions.hideModal());
    dispatch(
      features.layersManager.actions.setLayerIsActive({
        params: { alias },
        fields: { isActive: isAtLeastOneVisibleProvidersMarkers }
      })
    );
    dispatch(features.layersManager.actions.clearProvidersState(alias));
  }, [dispatch, onHide, providers, alias, isAtLeastOneVisibleProvidersMarkers]);

  if (isFolderView) {
    return (
      <ModalWrapper
        className={styles["modal-container-folder"]}
        isDisableClose={isDisableClose}
        onHide={onClose}
      >
        <div className={styles["modal"]}>
          <div className={styles["modal__text-container"]}>
            <h2 className={styles["modal__title"]}>
              <div onClick={onCloseFolder} className={styles["modal__back"]}>
                <img src={ArrowLeftIcon} alt="arrow-back" />
              </div>{" "}
              {title}
            </h2>
            <h4 className={styles["modal__description"]}>Folder files</h4>
            <ProviderFolder
              id={id}
              alias={alias}
              folderId={folderId}
              setFolderId={setFolderId}
            />
          </div>
          <div className={styles["modal__controls-wrapper"]}>
            <PrimaryButton type="button" onClick={onClose} textSize={14}>
              Done
            </PrimaryButton>
          </div>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      className={styles["modal-container"]}
      isDisableClose={isDisableClose}
      onHide={onClose}
    >
      <div className={styles["modal"]}>
        <div className={styles["modal__text-container"]}>
          <h2 className={styles["modal__title"]}>{title}</h2>
          <h4 className={styles["modal__description"]}>
            {description ?? "Manage files"}
          </h4>
          <div className={styles["modal__providers-table"]}>
            <div className={styles["providers-table__head"]}>
              <div className={styles["providers-table__head-controls"]}>
                {/* <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersMarkers}
                  isActive={isAtLeastOneVisibleProvider}
                  enabledIcon={EyeOpenGrayIcon}
                  disabledIcon={EyeCloseGrayIcon}
                /> */}
                <ShowHideButton
                  onClick={onUpdateVisibilityAllProvidersMarkers}
                  isActive={isAtLeastOneVisibleProvidersMarkers}
                  enabledIcon={MarkersFlagOnIcon}
                  disabledIcon={MarkersFlagOffIcon}
                />
              </div>
              <div>Name</div>
              <div>Action</div>
              <div>Time</div>
              <div>Inference action</div>
              <div>Time</div>
              <div>Inference status</div>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
              modifiers={[
                restrictToVerticalAxis,
                restrictToParentVerticalBounds
              ]}
            >
              <SortableContext
                items={providersIds}
                strategy={verticalListSortingStrategy}
              >
                {providers.length > 0 &&
                  providers.map((provider) => {
                    return (
                      <div key={`${provider.id}`} id={provider.id}>
                        <SortableItem
                          key={provider.id}
                          item={provider}
                          id={id}
                          alias={alias}
                          onDelete={onDelete}
                          onUpdateVisibilityProvidersMarkers={
                            onUpdateVisibilityProvidersMarkers
                          }
                          inferenceDurationTime={inferenceDurationTime}
                          uploadDurationTime={uploadDurationTime}
                          onOpenFolder={onOpenFolder}
                          onPrepareProvider={onPrepareProvider}
                        />
                      </div>
                    );
                  })}
              </SortableContext>
            </DndContext>
            {hasPolicy(UserPolices.PROJECT_PROVIDER_CREATE) && (
              <div className={styles["modal__upload"]}>
                <label className={styles["label"]}>
                  Please create a folder
                </label>
                <CommonButton isSmall icon={PlusIcon} onClick={onCreateFolder}>
                  Create folder
                </CommonButton>
                {/* <label className={styles["label"]}>
                  Please select a file to upload
                </label> */}
                {/* <UploadFiles title="Upload zip" handleFileChange={onSubmit} /> */}
              </div>
            )}
          </div>
        </div>
        <div className={styles["modal__controls-wrapper"]}>
          <PrimaryButton type="button" onClick={onClose} textSize={14}>
            Done
          </PrimaryButton>
        </div>
      </div>
    </ModalWrapper>
  );
};
export default ProviderFoldersFilesManagerModal;
